import { history } from 'instantsearch.js/es/lib/routers';
import type { UiState } from 'instantsearch.js';

/**
 * Custom routing configuration for Algolia InstantSearch on sequences
 * Maps clean URL parameters to Algolia UI state
 *
 * URL Format: /sequences/search?q=christmas&holiday=Christmas&software=xLights
 * Instead of Algolia default: /sequences/search?sequences[query]=christmas
 */

interface RouteState {
  q?: string;
  page?: number;
  software?: string[];
  holiday?: string[];
  genres?: string[];
  artist?: string[];
  creator?: string[];
  album?: string[];
  is_free?: string[];
  price_min?: string;
  price_max?: string;
  sort?: string;
}

interface SequencesUiState {
  query?: string;
  page?: number;
  refinementList?: {
    software_type?: string[];
    holiday_type?: string[];
    genres?: string[];
    artist_name?: string[];
    creator_name?: string[];
    album?: string[];
    is_free?: string[];
  };
  range?: {
    price?: {
      min?: number;
      max?: number;
    };
  };
  sortBy?: string;
}

/**
 * Convert Algolia UI state to URL parameters
 */
const stateToRoute = (uiState: UiState): RouteState => {
  const indexUiState = uiState['sequences'] as SequencesUiState | undefined;

  if (!indexUiState) {
    return {};
  }

  const route: RouteState = {};

  // Map query
  if (indexUiState.query) {
    route.q = indexUiState.query;
  }

  // Map page
  if (indexUiState.page && indexUiState.page > 1) {
    route.page = indexUiState.page;
  }

  // Map refinement lists (filters)
  if (indexUiState.refinementList) {
    const { refinementList } = indexUiState;

    if (refinementList.software_type?.length) {
      route.software = refinementList.software_type;
    }

    if (refinementList.holiday_type?.length) {
      route.holiday = refinementList.holiday_type;
    }

    if (refinementList.genres?.length) {
      route.genres = refinementList.genres;
    }

    if (refinementList.artist_name?.length) {
      route.artist = refinementList.artist_name;
    }

    if (refinementList.creator_name?.length) {
      route.creator = refinementList.creator_name;
    }

    if (refinementList.album?.length) {
      route.album = refinementList.album;
    }

    if (refinementList.is_free?.length) {
      route.is_free = refinementList.is_free;
    }
  }

  // Map price range
  if (indexUiState.range?.price) {
    const { min, max } = indexUiState.range.price;

    if (min !== undefined) {
      route.price_min = String(min);
    }

    if (max !== undefined) {
      route.price_max = String(max);
    }
  }

  // Map sorting
  if (indexUiState.sortBy) {
    route.sort = indexUiState.sortBy;
  }

  return route;
};

/**
 * Convert URL parameters to Algolia UI state
 */
const routeToState = (routeState: RouteState): UiState => {
  const indexUiState: SequencesUiState = {};

  // Map query
  if (routeState.q) {
    indexUiState.query = routeState.q;
  }

  // Map page
  if (routeState.page) {
    indexUiState.page = routeState.page;
  }

  // Initialize refinementList
  const refinementList: SequencesUiState['refinementList'] = {};
  let hasRefinements = false;

  // Map refinement lists (filters)
  if (routeState.software) {
    refinementList.software_type = Array.isArray(routeState.software)
      ? routeState.software
      : [routeState.software];
    hasRefinements = true;
  }

  if (routeState.holiday) {
    refinementList.holiday_type = Array.isArray(routeState.holiday)
      ? routeState.holiday
      : [routeState.holiday];
    hasRefinements = true;
  }

  if (routeState.genres) {
    refinementList.genres = Array.isArray(routeState.genres)
      ? routeState.genres
      : [routeState.genres];
    hasRefinements = true;
  }

  if (routeState.artist) {
    refinementList.artist_name = Array.isArray(routeState.artist)
      ? routeState.artist
      : [routeState.artist];
    hasRefinements = true;
  }

  if (routeState.creator) {
    refinementList.creator_name = Array.isArray(routeState.creator)
      ? routeState.creator
      : [routeState.creator];
    hasRefinements = true;
  }

  if (routeState.album) {
    refinementList.album = Array.isArray(routeState.album)
      ? routeState.album
      : [routeState.album];
    hasRefinements = true;
  }

  if (routeState.is_free) {
    refinementList.is_free = Array.isArray(routeState.is_free)
      ? routeState.is_free
      : [routeState.is_free];
    hasRefinements = true;
  }

  if (hasRefinements) {
    indexUiState.refinementList = refinementList;
  }

  // Map price range
  if (routeState.price_min !== undefined || routeState.price_max !== undefined) {
    indexUiState.range = {
      price: {
        ...(routeState.price_min !== undefined && { min: Number(routeState.price_min) }),
        ...(routeState.price_max !== undefined && { max: Number(routeState.price_max) }),
      },
    };
  }

  // Map sorting
  if (routeState.sort) {
    indexUiState.sortBy = routeState.sort;
  }

  return {
    sequences: indexUiState,
  };
};

/**
 * Export the complete routing configuration for sequences index
 */
export const sequencesRouting = {
  router: history(),
  stateMapping: {
    stateToRoute,
    routeToState,
  },
};
